import { useEffect, useState } from "react";
import api, { formatEuro } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CookingPot, BowlFood, Package, Carrot, CurrencyEur, Warning, ArrowDown, ArrowUp, Receipt, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const StatCard = ({ icon: Icon, label, value, testId, delay }) => (
  <div className={`bg-white border border-[#E5E5E0] rounded-2xl p-4 sm:p-6 fade-in ${delay}`} data-testid={testId}>
    <div className="h-10 w-10 rounded-xl bg-[#FAF9F6] border border-[#E5E5E0] flex items-center justify-center mb-4">
      <Icon size={22} weight="duotone" className="text-[#D95D39]" />
    </div>
    <p className="text-2xl sm:text-3xl font-extrabold" style={{ fontFamily: "Manrope" }}>{value}</p>
    <p className="text-sm text-[#6B7280] mt-1">{label}</p>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats)
    return <div className="text-[#6B7280]" data-testid="dashboard-loading">Caricamento...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold">Dashboard</h1>
        <p className="text-[#6B7280] mt-1">Panoramica di RisoRice: menu, scorte e movimenti.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={CookingPot} label="Piatti" value={stats.n_dishes} testId="stat-piatti" delay="stagger-1" />
        <StatCard icon={BowlFood} label="Bowl" value={stats.n_bowls} testId="stat-bowl" delay="stagger-1" />
        <StatCard icon={Package} label="Combo" value={stats.n_combos} testId="stat-combo" delay="stagger-2" />
        <StatCard icon={Carrot} label="Ingredienti" value={stats.n_ingredients} testId="stat-ingredienti" delay="stagger-3" />
        <StatCard icon={CurrencyEur} label="Valore magazzino" value={formatEuro(stats.valore_magazzino)} testId="stat-valore" delay="stagger-4" />
        <StatCard icon={Receipt} label="Vendite oggi" value={stats.vendite_oggi} testId="stat-vendite" delay="stagger-4" />
        <StatCard icon={ChartLineUp} label="Incasso oggi" value={formatEuro(stats.incasso_oggi)} testId="stat-incasso" delay="stagger-4" />
      </div>

      {stats.low_stock.length > 0 && (
        <div className="bg-white border border-red-200 rounded-2xl p-6 fade-in" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 mb-4">
            <Warning size={22} weight="fill" className="text-[#D32F2F]" />
            <h2 className="text-lg font-bold">Scorte in esaurimento</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {stats.low_stock.map((i) => (
              <Badge key={i.id} className="bg-red-100 text-red-800 hover:bg-red-100 rounded-full px-4 py-1.5 text-sm font-medium" data-testid={`low-stock-${i.id}`}>
                {i.nome}: {i.scorta} {i.unita} (min {i.soglia_minima})
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-[#E5E5E0] rounded-2xl p-6 fade-in" data-testid="stock-chart">
          <h2 className="text-lg font-bold mb-6">Livelli scorte</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.stock_chart} margin={{ left: 0, right: 0 }}>
              <XAxis dataKey="nome" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} width={45} />
              <Tooltip formatter={(v, n, p) => [`${v} ${p.payload.unita}`, "Scorta"]} />
              <Bar dataKey="scorta" radius={[6, 6, 0, 0]}>
                {stats.stock_chart.map((e) => (
                  <Cell key={e.id} fill={e.soglia_minima > 0 && e.scorta <= e.soglia_minima ? "#D32F2F" : "#5D735D"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E5E5E0] rounded-2xl p-6 fade-in" data-testid="recent-movements">
          <h2 className="text-lg font-bold mb-4">Ultimi movimenti</h2>
          <div className="space-y-3">
            {stats.recent_movements.length === 0 && <p className="text-sm text-[#6B7280]">Nessun movimento registrato.</p>}
            {stats.recent_movements.map((m) => (
              <div key={m.id} className="flex items-center gap-3 py-2 border-b border-[#E5E5E0] last:border-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${m.tipo === "carico" ? "bg-green-100" : "bg-orange-100"}`}>
                  {m.tipo === "carico" ? <ArrowDown size={16} className="text-green-700" /> : <ArrowUp size={16} className="text-orange-700" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.ingrediente_nome}</p>
                  <p className="text-xs text-[#6B7280]">{m.tipo === "carico" ? "Carico" : "Scarico"} · {m.quantita} {m.unita}</p>
                </div>
                <p className="text-xs text-[#6B7280] shrink-0">{new Date(m.created_at).toLocaleDateString("it-IT")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
